import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      placed: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-cyan-100 text-cyan-800',
      in_transit: 'bg-yellow-100 text-yellow-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1 bg-slate-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold text-primary mb-8 uppercase tracking-tight" data-testid="orders-heading">
            My Orders
          </h1>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-sm shadow-sm p-12 text-center" data-testid="no-orders">
              <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-primary mb-2 uppercase">No orders yet</h2>
              <p className="text-slate-600 mb-6">Start shopping to see your orders here</p>
              <Link to="/products">
                <Button>Browse Products</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="orders-list">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white rounded-sm shadow-sm p-6 hover:shadow-md transition-shadow"
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">
                        Order ID: <span className="font-mono font-medium text-primary">{order.id.slice(0, 8)}</span>
                      </p>
                      <p className="text-sm text-slate-600">
                        Placed on: {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-4 py-2 rounded-sm text-sm font-bold uppercase ${getStatusColor(order.order_status)}`} data-testid={`order-status-${order.id}`}>
                      {order.order_status.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="border-t border-slate-200 pt-4 mb-4">
                    <div className="space-y-2">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-slate-600">{item.name} x {item.quantity}</span>
                          <span className="font-medium">₹{(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                    <div>
                      <span className="text-sm text-slate-600">Total: </span>
                      <span className="text-xl font-bold text-secondary">₹{order.total.toFixed(2)}</span>
                    </div>
                    <Link to={`/orders/${order.id}`}>
                      <Button size="sm" className="gap-2" data-testid={`track-order-${order.id}`}>
                        Track Order <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Orders;